"use client";

import { useCallback, useState, useEffect, useRef } from "react";
import { useDropzone, Accept } from "react-dropzone";
import { Upload, Camera } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Menu } from "@/types/menu";
import { processMenuImage } from "@/lib/gemini/client";
import { parseMenu } from "@/lib/menuParser";
import { usePathname } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { useUserStore } from "@/store/userStore";
import { AuthDialog } from "./AuthDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoadingOverlay } from "./LoadingOverlay";
import { useRouter } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/mixpanel-client";

type FileType = "image" | "pdf" | "all";

function sanitizeFileName(fileName: string): string {
  // Get the file extension
  const extension = fileName.split(".").pop()?.toLowerCase() || "";

  // Create a URL-safe base name by:
  // 1. Encoding the original filename to handle all Unicode characters
  // 2. Removing problematic characters but keeping hyphens and underscores
  const safeName = encodeURIComponent(
    fileName.slice(0, fileName.lastIndexOf("."))
  )
    .replace(/%/g, "")
    .replace(/[^a-zA-Z0-9-_]/g, "-")
    .replace(/-+/g, "-") // Replace multiple consecutive hyphens with a single one
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens

  // Create a unique filename with timestamp
  const timestamp = Date.now();
  const uniqueName = `${timestamp}-${safeName || "untitled"}.${extension}`;

  return uniqueName;
}

function CreditErrorDialog({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const router = useRouter();
  const t = useTranslations("MenuUpload");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("insufficientCredits")}</DialogTitle>
          <DialogDescription>{t("noCreditsDescription")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 mt-4">
          <Button
            onClick={() => router.push("/dashboard/settings/plan" as const)}
            className="w-full"
          >
            {t("upgradePlan")}
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              router.push("/dashboard/settings/plan#purchase-credits")
            }
            className="w-full"
          >
            {t("purchaseCredits")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add a utility function to detect mobile devices
function isMobileDevice() {
  if (typeof window === "undefined") return false;

  // Check if the device has touch capability and a mobile user agent
  const hasTouchScreen =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - IE/Edge specific property
    navigator.msMaxTouchPoints > 0;

  const isMobileUserAgent =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  return hasTouchScreen && isMobileUserAgent;
}

export default function MenuUpload({
  onUploadComplete,
  fileType = "all",
  preSignup = false,
  onProcessingStart,
  onProcessingComplete,
}: {
  onUploadComplete: (menu: Menu) => void;
  fileType?: FileType;
  preSignup?: boolean;
  onProcessingStart?: (progress: {
    fileUploaded: boolean;
    analyzing: boolean;
    extracting: boolean;
    creating: boolean;
  }) => void;
  onProcessingComplete?: () => void;
}) {
  const [uploadProgress, setUploadProgress] = useState({
    fileUploaded: false,
    analyzing: false,
    extracting: false,
    creating: false,
  });
  const [showCreditError, setShowCreditError] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { toast } = useToast();
  const pathname = usePathname();
  const supabase = createClient();
  const { getRemainingCredits } = useUserStore();
  const uploadIdRef = useRef<string>(`upload-${Date.now()}`);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingUploadId, setPendingUploadId] = useState<string | null>(null);
  const t = useTranslations("MenuUpload");

  // Check for mobile device on mount
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, [pathname]);

  // Helper function to update progress
  const updateProgress = useCallback(
    (progress: typeof uploadProgress) => {
      setUploadProgress(progress);
      onProcessingStart?.(progress);
    },
    [onProcessingStart]
  );

  // Handle dashboard navigation with proper cleanup
  const navigateToDashboard = useCallback(
    (menuId?: string) => {
      // Log that we're trying to navigate
      console.log(
        `NAVIGATION: Redirecting to ${
          menuId ? `/dashboard/menu/${menuId}` : "/dashboard"
        }`
      );

      // Hide loading overlay before navigation
      setShowLoadingOverlay(false);

      // Call the completion callback first
      onProcessingComplete?.();

      // Force navigation with window.location for most reliable redirect
      // This is an alternative to router.push that's more forceful
      if (menuId) {
        console.log(
          `NAVIGATION: Using window.location to /dashboard/menu/${menuId}`
        );
        window.location.href = `/dashboard/menu/${menuId}`;
      } else {
        console.log(`NAVIGATION: Using window.location to /dashboard`);
        window.location.href = `/dashboard`;
      }
    },
    [onProcessingComplete]
  );

  const getAcceptedTypes = useCallback((type: FileType): Accept => {
    switch (type) {
      case "image":
        return {
          "image/jpeg": [".jpg", ".jpeg"],
          "image/png": [".png"],
        };
      case "pdf":
        return {
          "application/pdf": [".pdf"],
        };
      case "all":
      default:
        return {
          "image/jpeg": [".jpg", ".jpeg"],
          "image/png": [".png"],
          "application/pdf": [".pdf"],
        };
    }
  }, []);

  // Store file in persistent storage
  const storeFileForAuth = useCallback(async (file: File) => {
    try {
      // Generate a unique ID for this upload
      const uploadId = uploadIdRef.current;

      console.log("MenuUpload: Storing file for auth", {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        uploadId,
      });

      // Read the file as a data URL (Base64)
      const fileReader = new FileReader();
      fileReader.onload = () => {
        try {
          // Store file metadata and data in sessionStorage
          const fileData = {
            name: file.name,
            type: file.type,
            size: file.size,
            data: fileReader.result, // This will be a data URL (Base64 encoded)
            timestamp: Date.now(),
            attempts: 0, // Track processing attempts
            expiresAt: Date.now() + 30 * 60 * 1000, // Expire after 30 minutes
          };

          // Store the file data in sessionStorage only
          const fileDataJson = JSON.stringify(fileData);

          console.log("MenuUpload: File data prepared", {
            dataSize: fileDataJson.length,
            uploadId,
            expiresAt: new Date(fileData.expiresAt).toISOString(),
          });

          // Set pending upload ID in state
          setPendingUploadId(uploadId);

          try {
            // Store in sessionStorage only
            sessionStorage.setItem(`pendingFile_${uploadId}`, fileDataJson);
            sessionStorage.setItem("pendingAuthUpload", uploadId);
            console.log("MenuUpload: File stored in sessionStorage");
          } catch (storageError) {
            console.error("Error storing file in storage:", storageError);
            // If storage fails due to quota or permission issues, try with a smaller version
            if (
              storageError instanceof DOMException &&
              (storageError.name === "QuotaExceededError" ||
                storageError.message.includes("quota"))
            ) {
              console.log(
                "MenuUpload: Storage quota exceeded, trying with smaller data"
              );

              // Create a simplified version with just the essential info (no file data)
              const minimalData = {
                name: file.name,
                type: file.type,
                timestamp: Date.now(),
                attempts: 0,
                expiresAt: Date.now() + 30 * 60 * 1000,
                isMinimal: true, // Flag to indicate this is minimal data
              };

              // Store the minimal data only in sessionStorage
              sessionStorage.setItem(
                `pendingMinimal_${uploadId}`,
                JSON.stringify(minimalData)
              );
              sessionStorage.setItem("pendingAuthUpload", uploadId);
              console.log("MenuUpload: Saved minimal data with uploadId");
            }
          }

          // Verify storage worked correctly
          try {
            const storedInSession = sessionStorage.getItem(
              `pendingFile_${uploadId}`
            );
            const pendingIdSession =
              sessionStorage.getItem("pendingAuthUpload");

            console.log("MenuUpload: Storage verification", {
              sessionStorage: !!storedInSession,
              pendingIdSession,
              dataLengthSession: storedInSession?.length ?? 0,
            });
          } catch (e) {
            console.error("Error verifying storage:", e);
          }

          // Show auth dialog after file is stored
          setShowAuthDialog(true);
        } catch (error) {
          console.error("Error storing file data:", error);
          setShowAuthDialog(true);
        }
      };

      fileReader.onerror = () => {
        console.error("Error reading file");
        setShowAuthDialog(true);
      };

      // Read file as data URL (Base64)
      fileReader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing file for storage:", error);
      setShowAuthDialog(true);
    }
  }, []);

  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up any stored files when component unmounts
      const uploadId = localStorage.getItem("pendingAuthUpload");
      if (uploadId) {
        localStorage.removeItem(`pendingFile_${uploadId}`);
        localStorage.removeItem("pendingAuthUpload");
      }
    };
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      if (!file) return;

      // Validate file type
      const validTypes = Object.keys(getAcceptedTypes(fileType));
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `Please upload ${
            fileType === "image"
              ? "a JPG or PNG file"
              : fileType === "pdf"
              ? "a PDF file"
              : "a JPG, PNG, or PDF file"
          }.`,
          variant: "destructive",
        });
        return;
      }

      try {
        // Get current session
        const {
          data: { session },
        } = await supabase.auth.getSession();

        // If user is not authenticated, store file and show auth dialog
        if (!session) {
          // Store file in localStorage/IndexedDB and show auth dialog
          await storeFileForAuth(file);
          return;
        }

        // Track Menu Upload Initiated
        trackEvent("Menu Upload Initiated", {
          "File Type": file.type,
          "File Size": file.size,
        });

        // Skip credit check for pre-signup flow
        if (!preSignup) {
          const remainingCredits = getRemainingCredits();
          if (remainingCredits === 0) {
            setShowCreditError(true);
            return;
          }
        }

        setShowLoadingOverlay(true);

        // Update progress state
        const newProgress = {
          fileUploaded: true,
          analyzing: false,
          extracting: false,
          creating: false,
        };
        updateProgress(newProgress);

        // Track Menu Processing Started
        trackEvent("Menu Processing Started", {
          "File Type": file.type,
        });

        // Process the menu
        const result = await processMenuImage(file);

        if ("cancelled" in result) {
          // Track Menu Processing Cancelled
          trackEvent("Menu Processing Cancelled");

          setShowLoadingOverlay(false);
          onProcessingComplete?.();
          return;
        }

        // Update progress
        updateProgress({ ...uploadProgress, analyzing: true });

        // For pre-signup flow, we don't save to Supabase
        if (preSignup) {
          // Create a temporary menu object
          const menu = parseMenu(
            result.originalText,
            "temp",
            result.detectedLanguage
          );
          onUploadComplete(menu);
          onProcessingComplete?.();
          return;
        }

        // Create a sanitized file name
        const sanitizedFileName = sanitizeFileName(file.name);

        // Update progress
        updateProgress({
          ...uploadProgress,
          analyzing: true,
          extracting: true,
        });

        // Upload the image to Supabase Storage with sanitized name
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("menus")
          .upload(sanitizedFileName, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);

          // Track Menu Processing Failed
          trackEvent("Menu Processing Failed", {
            Error: uploadError.message,
            Stage: "Upload",
          });

          throw new Error(
            uploadError.message || "Failed to upload file to storage"
          );
        }

        // Get the public URL for the uploaded image
        const {
          data: { publicUrl },
        } = supabase.storage.from("menus").getPublicUrl(uploadData.path);

        // Update progress
        updateProgress({
          fileUploaded: true,
          analyzing: true,
          extracting: true,
          creating: true,
        });

        // Create a menu object
        const menu = parseMenu(
          result.originalText,
          "temp",
          result.detectedLanguage
        );

        // Track Menu Processing Succeeded and Credits Consumed
        trackEvent("Menu Processing Succeeded", {
          "Detected Language": result.detectedLanguage,
          "Categories Count": result.menu.categories.length,
          "Items Count": result.menu.categories.reduce(
            (sum, category) => sum + category.items.length,
            0
          ),
        });

        trackEvent("Credits Consumed", {
          "Credits Used": 1,
          Feature: "Menu Processing",
        });

        // Ensure all required fields are present and language is properly set
        const detectedLanguage = result.detectedLanguage.toLowerCase();

        // Set the detected language as the only supported language
        menu.supportedLanguages = [detectedLanguage];

        menu.title = {
          [detectedLanguage]: result.menu.title.text || "Menu",
        };

        menu.categories = result.menu.categories.map((category) => ({
          id: category.id,
          name: {
            [detectedLanguage]: category.name.text,
          },
          items: category.items.map((item) => ({
            id: item.id,
            name: {
              [detectedLanguage]: item.name.text,
            },
            description: item.description
              ? {
                  [detectedLanguage]: item.description.text,
                }
              : undefined,
            price: {
              amount: item.price.amount,
              taxIncluded: item.price.taxIncluded || undefined,
            },
            category: category.id,
          })),
        }));

        menu.status = "completed";
        menu.originalImageUrl = publicUrl;

        // Insert the menu into Supabase
        const { data: menuData, error: insertError } = await supabase
          .from("menus")
          .insert({
            structured_data: menu,
            status: "completed",
            original_text: result.originalText,
            original_image_url: publicUrl,
            user_id: session?.user?.id || null,
          })
          .select()
          .single();

        if (insertError) {
          console.error("Insert error:", insertError);
          throw insertError;
        }

        // Call the callback with the menu data
        onUploadComplete(menu);

        // After successfully creating the menu...
        // Make sure we properly redirect to the menu
        if (menuData?.id) {
          console.log(
            "Menu created successfully, navigating to:",
            `/dashboard/menu/${menuData.id}`
          );
          // Use the navigation helper function
          navigateToDashboard(menuData.id);
        } else {
          console.error("Menu data is missing ID after creation");
        }
      } catch (error) {
        console.error("Error processing menu:", error);

        // Track Menu Processing Failed
        trackEvent("Menu Processing Failed", {
          Error: error instanceof Error ? error.message : "Unknown error",
        });

        setShowLoadingOverlay(false);
        onProcessingComplete?.();

        if (
          error instanceof Error &&
          error.message.includes("Insufficient credits")
        ) {
          setShowCreditError(true);
        } else {
          toast({
            variant: "destructive",
            title: "Error processing menu",
            description:
              error instanceof Error
                ? error.message
                : "Please try again with a clearer file.",
          });
        }
      } finally {
        setUploadProgress({
          fileUploaded: false,
          analyzing: false,
          extracting: false,
          creating: false,
        });
      }
    },
    [
      fileType,
      getAcceptedTypes,
      toast,
      supabase,
      storeFileForAuth,
      preSignup,
      getRemainingCredits,
      setShowCreditError,
      setShowLoadingOverlay,
      updateProgress,
      uploadProgress,
      onProcessingComplete,
      onUploadComplete,
      navigateToDashboard,
    ]
  );

  // Function to handle camera button click
  const captureImage = async () => {
    try {
      // For mobile devices, prefer the native camera approach
      if (isMobile) {
        // Create a temporary file input to trigger the camera
        const tempInput = document.createElement("input");
        tempInput.type = "file";
        tempInput.accept = "image/*";
        tempInput.setAttribute("capture", "camera"); // This will open the camera on mobile

        // Handle the file selection
        tempInput.onchange = async (e) => {
          const files = (e.target as HTMLInputElement).files;
          if (files && files.length > 0) {
            await handleFile(files[0]);
          }
        };

        // Trigger the file input
        tempInput.click();
        return;
      }

      // Fallback to MediaDevices API for desktop browsers
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Create video element to show stream
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();

      // Create canvas to capture frame
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      context?.drawImage(video, 0, 0);

      // Convert to file
      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], "menu-capture.jpg", {
            type: "image/jpeg",
          });
          await handleFile(file);
        }
        // Stop camera stream
        stream.getTracks().forEach((track) => track.stop());
      }, "image/jpeg");
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast({
        title: "Camera Error",
        description:
          "Could not access camera. Please try uploading a file instead.",
        variant: "destructive",
      });
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        await handleFile(file);
      }
    },
    [handleFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getAcceptedTypes(fileType),
    maxFiles: 1,
  });

  const handleAuthSuccess = () => {
    setShowAuthDialog(false);

    // Hide any loading overlay
    setShowLoadingOverlay(false);

    // Just redirect to dashboard - it will check for pendingAuthUpload
    // We no longer need to use URL parameters as we're storing the uploadId in localStorage
    window.location.href = "/dashboard";
  };

  // Display appropriate text based on file type
  const getFileTypeText = () => {
    if (fileType === "image") {
      return t("supportsImageFiles");
    } else if (fileType === "pdf") {
      return t("supportsPdfFiles");
    } else {
      return t("supportsAllFiles");
    }
  };

  return (
    <div className="relative h-full w-full">
      <div className="space-y-4 h-full w-full">
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors h-full flex flex-col items-center justify-center dropzone-container bg-white",
            isDragActive
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 dark:border-primary/30"
          )}
        >
          <input {...getInputProps()} />
          <div className="space-y-4 flex flex-col items-center justify-center">
            <div>
              <Upload className="w-8 h-8 mx-auto mb-4 text-primary" />
              <h3 className="text-lg font-medium text-gray-800">
                {t("uploadMenu")}
              </h3>
              {/* Only show drag and drop text on non-mobile devices */}
              {!isMobile && (
                <p className="text-sm text-gray-600 mt-1">{t("dragAndDrop")}</p>
              )}
              <p className="text-xs text-gray-500 mt-1 mb-4">
                {getFileTypeText()}
              </p>

              {/* Adding explicit buttons for accessibility */}
              <div className="flex flex-col sm:flex-row gap-3 mt-2 justify-center">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    const fileInput =
                      document.querySelector('input[type="file"]');
                    if (fileInput) (fileInput as HTMLInputElement).click();
                  }}
                >
                  <Upload className="h-4 w-4 text-white" />
                  <span className="text-white">{t("selectFile")}</span>
                </Button>

                {/* Only show camera button on mobile devices */}
                {isMobile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 border-primary/30 text-primary hover:bg-primary/5 bg-white dark:bg-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      captureImage();
                    }}
                    aria-label="Take Photo"
                  >
                    <Camera className="h-4 w-4" />
                    <span>{t("takePhoto")}</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Hidden file input specifically for camera capture */}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          id="camera-input"
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) {
              handleFile(files[0]);
            }
          }}
        />

        {/* Hidden button that's always available for the homepage button to trigger */}
        <Button
          type="button"
          className="hidden"
          variant="outline"
          onClick={captureImage}
          aria-label="Take Photo"
        >
          <Camera className="h-4 w-4 mr-2" />
          {t("takePhoto")}
        </Button>
      </div>

      <CreditErrorDialog
        isOpen={showCreditError}
        onClose={() => setShowCreditError(false)}
      />

      <AuthDialog
        isOpen={showAuthDialog}
        onClose={() => {
          setShowAuthDialog(false);
        }}
        onSuccess={handleAuthSuccess}
        pendingUploadId={uploadIdRef.current}
      />

      {showLoadingOverlay && <LoadingOverlay progress={uploadProgress} />}
    </div>
  );
}
